// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Check, ClipboardPlus, Database } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { useRipple } from "@/hooks/use-ripple";

interface IdPillProps {
  id: number | string;
}

export function IdPill({ id }: IdPillProps) {
  const { t } = useTranslation("back-office");
  const [copied, setCopied] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useRipple(ref);

  function handleCopy(e: React.MouseEvent) {
    e.stopPropagation();
    navigator.clipboard.writeText(String(id));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast(
      <span>
        {t("common.copiedToClipboard")} <b>{id}</b>
      </span>,
    );
  }

  return (
    <Badge
      ref={ref}
      variant="secondary"
      className="font-mono font-normal gap-1.5 cursor-pointer select-none [&>svg]:size-2.5 relative overflow-hidden"
      onClick={handleCopy}
    >
      <Database className="opacity-50" />
      id: {id}
      {copied ? <Check className="text-emerald-600" /> : <ClipboardPlus className="opacity-50" />}
    </Badge>
  );
}
